import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { AboutSection } from "@/components/sections/section-about";
import { BannerSection } from "@/components/sections/section-banner";
import {
  TransportSection,
  TransportSectionDefaults,
} from "@/components/sections/section-transport";
import { Button } from "@/components/ui/button";
import {
  BlogPost,
  blogPosts,
  getAllCategories,
} from "@/data/blog-posts";
import { ArrowRight, Clock, Tag } from "lucide-react";

const BASE_URL = import.meta.env.VITE_APP_URL;

function BlogCard({ post, featured = false }: { post: BlogPost; featured?: boolean }) {
  if (featured) {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group block bg-white rounded-2xl border border-n-beige overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:bg-n-beige"
      >
        <div className="aspect-[16/7] bg-gray-100 overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/mudanca.svg";
            }}
          />
        </div>
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-xs font-medium text-n-text-green bg-green-50 px-3 py-1 rounded-full flex items-center gap-1">
              <Tag className="h-3 w-3" />
              {post.category}
            </span>
            <span className="text-xs text-n-text-neutral flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readTime}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-n-text mb-3 group-hover:text-n-text-green transition-colors line-clamp-2">
            {post.title}
          </h2>
          <p className="text-n-text-neutral mb-5 line-clamp-2">{post.excerpt}</p>
          <div className="flex items-center gap-2 text-n-text-green font-medium text-sm group-hover:gap-3 transition-all">
            Leia mais
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white rounded-xl border border-n-beige overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:bg-n-beige flex flex-col"
    >
      <div className="aspect-video bg-gray-100 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/mudanca.svg";
          }}
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-medium text-n-text-green bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {post.category}
          </span>
          <span className="text-xs text-n-text-neutral flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {post.readTime}
          </span>
        </div>
        <h3 className="font-semibold text-lg text-n-text mb-2 group-hover:text-n-text-green transition-colors line-clamp-2 flex-1">
          {post.title}
        </h3>
        <p className="text-sm text-n-text-neutral mb-4 line-clamp-3">{post.excerpt}</p>
        <div className="flex items-center gap-2 text-n-text-green font-medium text-sm group-hover:gap-3 transition-all mt-auto">
          Leia mais
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

export default function BlogHomePage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = getAllCategories();

  const filtered = selectedCategory
    ? blogPosts.filter((p) => p.category === selectedCategory)
    : blogPosts;

  const [featured, ...rest] = filtered;

  return (
    <div className="min-h-screen bg-n-bg-1">
      <Helmet>
        <title>Blog | Nacional GO — Dicas e Novidades sobre Transporte de Veículos</title>
        <meta
          name="description"
          content="Dicas, guias e novidades sobre transporte de veículos com segurança e tecnologia. Saiba mais no blog da Nacional GO."
        />
        <link rel="canonical" href={`${BASE_URL}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta
          property="og:title"
          content="Blog | Nacional GO — Dicas e Novidades sobre Transporte de Veículos"
        />
        <meta
          property="og:description"
          content="Dicas, guias e novidades sobre transporte de veículos com segurança e tecnologia. Saiba mais no blog da Nacional GO."
        />
        <meta property="og:url" content={`${BASE_URL}/blog`} />
        <meta property="og:image" content={`${BASE_URL}/og/webclip.png`} />
        <meta property="og:site_name" content="Nacional GO" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Blog | Nacional GO — Dicas e Novidades sobre Transporte de Veículos"
        />
        <meta
          name="twitter:description"
          content="Dicas, guias e novidades sobre transporte de veículos com segurança e tecnologia."
        />
        <meta name="twitter:image" content={`${BASE_URL}/og/webclip.png`} />
      </Helmet>

      {/* Hero header */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container">
          <p className="text-sm text-n-text-neutral mb-3">&gt; Blog</p>
          <h1 className="text-n-text-green text-3xl font-semibold md:text-4xl lg:text-5xl max-w-3xl">
            Dicas e novidades sobre como transportar o seu veículo de maneira
            rápida e segura
          </h1>
        </div>
      </section>

      {/* Main content */}
      <section className="py-10 md:py-14">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* Sidebar — categories */}
            <aside className="lg:w-56 shrink-0">
              <div className="bg-white border border-n-beige rounded-xl p-5 lg:sticky lg:top-24">
                <p className="text-sm font-semibold text-n-text mb-3">Categorias</p>
                <ul className="flex lg:flex-col flex-row flex-wrap gap-1">
                  <li>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === null
                          ? "bg-n-green-h-light text-n-text-green font-medium"
                          : "text-n-text-neutral hover:bg-n-green-h-light hover:text-n-text-green"
                      }`}
                    >
                      Ver todas
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat}>
                      <button
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedCategory === cat
                            ? "bg-n-green-h-light text-n-text-green font-medium"
                            : "text-n-text-neutral hover:bg-n-green-h-light hover:text-n-text-green"
                        }`}
                      >
                        {cat}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Posts area */}
            <div className="flex-1 min-w-0">
              {filtered.length === 0 ? (
                <div className="text-center py-20 text-n-text-neutral">
                  Nenhum post encontrado nesta categoria.
                </div>
              ) : (
                <>
                  {/* Featured post */}
                  {featured && (
                    <div className="mb-8">
                      <BlogCard post={featured} featured />
                    </div>
                  )}

                  {/* Grid */}
                  {rest.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {rest.map((post) => (
                        <BlogCard key={post.id} post={post} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mobile "Ver todas" CTA */}
          {selectedCategory && (
            <div className="text-center mt-10 lg:hidden">
              <Button
                variant="outline"
                onClick={() => setSelectedCategory(null)}
                className="text-n-text-green border-n-text-green hover:bg-n-text-green hover:text-white"
              >
                Ver todos os artigos
              </Button>
            </div>
          )}
        </div>
      </section>

      <AboutSection />
      <TransportSection {...TransportSectionDefaults} />
      <BannerSection title="Faça agora uma cotação e agende o transporte do seu veículo !" />
    </div>
  );
}
